'use client';

import * as SubframeUtils from '@/shared/components/ui/utils';
import { formatCurrency, formatCpfCnpj } from '@/shared/formatters';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import {
  FeatherBriefcase,
  FeatherCheck,
  FeatherDollarSign,
  FeatherMessageSquare,
  FeatherUser,
} from '@subframe/core';

interface NegotiationSummaryCardProps {
  counterpartyName?: string;
  counterpartyCpfCnpj?: string;
  counterpartyPhone?: string;
  claimAmount?: number;
  objective?: string;
  agentName?: string;
  className?: string;
}

const objectiveLabels: Record<string, string> = {
  valores: 'Negociação de valores',
  contrato: 'Formalização de contrato',
  regularizacao: 'Regularização',
  prevencao: 'Comunicação preventiva',
};

export function NegotiationSummaryCard({
  counterpartyName,
  counterpartyCpfCnpj,
  counterpartyPhone,
  claimAmount,
  objective,
  agentName,
  className,
}: NegotiationSummaryCardProps) {
  const hasCounterpartyData = counterpartyName || counterpartyCpfCnpj || counterpartyPhone;
  const hasData = hasCounterpartyData || claimAmount || objective || agentName;

  return (
    <div
      className={SubframeUtils.twClassNames(
        'flex h-full flex-col overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-white shadow-sm',
        className,
      )}
    >
      <div className="flex flex-col gap-4 border-b border-solid border-neutral-100 bg-neutral-50 p-6">
        <div className="flex items-start gap-3">
          <div className="text-brand-600 flex h-10 w-10 flex-none items-center justify-center rounded-[12px] border border-solid border-neutral-100 bg-white shadow-sm">
            <FeatherBriefcase />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 className="text-body-bold font-body-bold text-default-font">
              Resumo da Negociação
            </h3>
            <span className="text-caption font-caption text-subtext-color">
              Configure os dados abaixo
            </span>
          </div>
        </div>

        {agentName && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <FeatherMessageSquare className="text-subtext-color h-3.5 w-3.5" />
              <span className="text-subtext-color text-[10px] font-medium tracking-wider uppercase">
                Agente Selecionado
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-body-bold font-body-bold text-default-font">{agentName}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-auto p-6">
        {!hasData ? (
          <div className="flex flex-col gap-2">
            <p className="text-caption text-subtext-color">
              Preencha o formulário ao lado para ver o resumo da negociação aqui.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h4 className="text-default-font text-[10px] font-bold tracking-wider uppercase">
                Dados Preenchidos
              </h4>
              <p className="text-caption text-subtext-color">
                Informações configuradas para a negociação.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {hasCounterpartyData && (
                <div className="flex gap-3 rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 p-3">
                  <div className="bg-brand-50 flex h-8 w-8 flex-none items-center justify-center rounded-[8px]">
                    <FeatherUser className="text-brand-600 h-4 w-4" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-caption font-caption text-subtext-color">
                      Contraparte
                    </span>
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {counterpartyName || '—'}
                    </span>
                    {counterpartyCpfCnpj && (
                      <span className="text-caption text-subtext-color">
                        {formatCpfCnpj(counterpartyCpfCnpj)}
                      </span>
                    )}
                    {counterpartyPhone && (
                      <span className="text-caption text-subtext-color">
                        WhatsApp: {formatTelefoneComCodigoPais(counterpartyPhone)}
                      </span>
                    )}
                  </div>
                  <FeatherCheck className="text-success-600 h-4 w-4 flex-none" />
                </div>
              )}

              {claimAmount !== undefined && claimAmount > 0 && (
                <div className="flex gap-3 rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 p-3">
                  <div className="bg-success-50 flex h-8 w-8 flex-none items-center justify-center rounded-[8px]">
                    <FeatherDollarSign className="text-success-600 h-4 w-4" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-caption font-caption text-subtext-color">Valor</span>
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {formatCurrency(claimAmount)}
                    </span>
                  </div>
                  <FeatherCheck className="text-success-600 h-4 w-4 flex-none" />
                </div>
              )}

              {objective && (
                <div className="flex gap-3 rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 p-3">
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-[8px] bg-purple-50">
                    <FeatherBriefcase className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-caption font-caption text-subtext-color">Objetivo</span>
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {objectiveLabels[objective] || objective}
                    </span>
                  </div>
                  <FeatherCheck className="text-success-600 h-4 w-4 flex-none" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
